import { ActionIcon, Badge, Code, Group, List, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { CAPABILITIES, capabilityLabel, setSlot, slotsOf, slotSummary } from './capabilities';

// The Capability step: what the connector answers. The model's tool is the platform's own, so
// there is nothing to write for the agent here.
export const CapabilityStep = ({ ws }) => {
  const { form, setField, errors, readOnly, contracts } = ws;
  const meta = CAPABILITIES[form.capability];

  return (
    <Stack gap="sm">
      <Select
        label="Answers"
        size="xs"
        maw={320}
        placeholder="Pick a capability"
        disabled={readOnly}
        error={errors.capability}
        data={contracts.map((contract) => ({
          value: contract.capability,
          label: capabilityLabel(contract.capability),
        }))}
        value={form.capability || null}
        onChange={(value) => setField('capability', value || '')}
      />
      {meta && <Text size="sm">{meta.gains}</Text>}
      {meta?.notes?.length > 0 && (
        <List size="xs" c="dimmed" spacing={2}>
          {meta.notes.map((note) => (
            <List.Item key={note}>{note}</List.Item>
          ))}
        </List>
      )}
      <Text size="xs" c="dimmed">
        The agent uses the platform&apos;s own tool for this; fill the slots under Rows from your API&apos;s answer.
      </Text>
    </Stack>
  );
};

// One fixed row per result field of the contract. A selected slot is filled by clicking a value in
// the response; typing the path is the fallback.
export const CapabilitySlots = ({ ws }) => {
  const { form, setField, readOnly, contract, slot, setSlot: select } = ws;
  if (!contract)
    return (
      <Text size="xs" c="dimmed">
        Pick what this connector answers under Capability to see its slots.
      </Text>
    );

  const slots = slotsOf(contract, form.mapRows);
  const summary = slotSummary(slots);
  const names = slots.map((field) => field.name);
  const strangers = form.mapRows.filter((row) => row.key.trim() && !names.includes(row.key.trim()));

  return (
    <Stack gap={4}>
      <Group gap="xs">
        <Text size="sm" fw={600}>
          {capabilityLabel(form.capability)}
        </Text>
        <Badge variant="light" color={summary.missing ? 'warning' : 'success'} data-testid="slot-summary">
          {summary.text}
        </Badge>
      </Group>
      <Table verticalSpacing={2} horizontalSpacing={4} fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Slot</Table.Th>
            <Table.Th>From the response</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {slots.map((field) => {
            const selected = slot === field.name;
            return (
              <Table.Tr
                key={field.name}
                data-testid={`slot-${field.name}`}
                bg={selected ? 'var(--mantine-primary-color-light)' : undefined}
                style={{ cursor: readOnly ? undefined : 'pointer' }}
                onClick={() => !readOnly && select(field.name)}
              >
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="xs" ff="monospace">
                      {field.name}
                    </Text>
                    {field.required && (
                      <Badge size="xs" variant="light" color="danger">
                        required
                      </Badge>
                    )}
                    <Text size="xs" c="dimmed">
                      {field.type}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label={`Path for ${field.name}`}
                    size="xs"
                    ff="monospace"
                    placeholder={selected ? 'click a value in the response' : ''}
                    disabled={readOnly}
                    value={field.path}
                    onFocus={() => select(field.name)}
                    onChange={(event) => setField('mapRows', setSlot(form.mapRows, field.name, event.target.value))}
                  />
                </Table.Td>
                <Table.Td c="success">{field.path.trim() ? '✓' : ''}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {strangers.map((row) => (
        <Group key={row.key} gap="xs">
          <Text size="xs" c="red">
            <Code>{row.key.trim()}</Code> is not a slot of {form.capability}.
          </Text>
          {!readOnly && (
            <ActionIcon
              size="xs"
              variant="subtle"
              color="danger"
              aria-label={`Remove ${row.key.trim()}`}
              onClick={() => setField('mapRows', setSlot(form.mapRows, row.key.trim(), ''))}
            >
              <IconTrash size={12} />
            </ActionIcon>
          )}
        </Group>
      ))}
    </Stack>
  );
};
